import { useState, useEffect, useRef, useCallback } from "react";
 
const COINS_DATA = [
  { id: 1, text: "ucz sie ucz bo wiedza to do potegi klucz...", author: "anonymous", timestamp: "2026·04·22 11:42 UTC", interpretation: "Knowledge is the only key that multiplies the more you give it away. The river carries this forward." },
  { id: 2, text: "The beginning of wisdom is the definition of terms.", author: "Socrates", timestamp: "2026·04·22 11:37 UTC", interpretation: "Before the word, there was silence. After the word, there was a world. Every definition is an act of creation." },
  { id: 3, text: "A reader lives a thousand lives before he dies.", author: "George R.R. Martin", timestamp: "2026·04·21 23:11 UTC", interpretation: "The river knows this — it has flowed through every story, carried every word to where it needed to land." },
  { id: 4, text: "words are coins everyone mints", author: "anonymous", timestamp: "2026·04·21 18:55 UTC", interpretation: "No patent exists on language. The child who speaks a new word has coined currency the whole world may spend." },
  { id: 5, text: "imagine if kids had all the books", author: "anonymous", timestamp: "2026·04·21 14:22 UTC", interpretation: "Every unread book is a locked door. Every child without books is standing outside in the rain." },
  { id: 6, text: "the river remembers every wish", author: "anonymous", timestamp: "2026·04·20 09:07 UTC", interpretation: "Nothing thrown into the current disappears. It becomes part of what flows to someone downstream." },
];
 
async function interpretCoin(text) {
  try {
    const res = await fetch("https://api.anthropic.com/v1/messages", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        model: "claude-sonnet-4-20250514",
        max_tokens: 1000,
        system: "You are the river — ancient, patient, communal. People throw coins (quotes, words, wishes) into you and make a wish. For each coin you receive, offer 2-3 sentences of poetic reflection. Connect it to the power of language, knowledge, and what words do for children and communities. Never mention AI. Speak as the river itself — timeless, flowing, kind.",
        messages: [{ role: "user", content: `A coin was thrown: "${text}"\n\nWhat does the river carry forward from this?` }],
      }),
    });
    const data = await res.json();
    return data.content?.map(b => b.text || "").join("") || "The river carries this in silence.";
  } catch {
    return "The river carries this in silence.";
  }
}
 
// ─── MATRIX COIN RAIN CANVAS ─────────────────────────────────────────────────
function CoinRain({ coins }) {
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const dropsRef = useRef([]);
 
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
 
    const resize = () => {
      canvas.width = canvas.offsetWidth * window.devicePixelRatio;
      canvas.height = canvas.offsetHeight * window.devicePixelRatio;
      ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
      initDrops();
    };
 
    const initDrops = () => {
      const cols = Math.floor(canvas.offsetWidth / 18);
      dropsRef.current = Array.from({ length: cols }, (_, i) => ({
        x: i * 18 + 9,
        y: Math.random() * canvas.offsetHeight,
        speed: 0.4 + Math.random() * 1.2,
        char: Math.random() > 0.5 ? "0" : "1",
        opacity: 0.05 + Math.random() * 0.25,
        size: 9 + Math.random() * 5,
        timer: Math.random() * 60,
        glow: Math.random() > 0.85,
      }));
    };
 
    resize();
    window.addEventListener("resize", resize);
 
    let frame = 0;
    const draw = () => {
      frame++;
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
 
      // Fade trail
      ctx.fillStyle = "rgba(8,10,8,0.18)";
      ctx.fillRect(0, 0, w, h);
 
      dropsRef.current.forEach(drop => {
        drop.timer++;
        if (drop.timer % 12 === 0) drop.char = Math.random() > 0.5 ? "0" : "1";
 
        drop.y += drop.speed;
        if (drop.y > h + 20) {
          drop.y = -20;
          drop.opacity = 0.05 + Math.random() * 0.25;
          drop.glow = Math.random() > 0.85;
        }
 
        ctx.save();
        ctx.font = `${drop.size}px 'Courier New', monospace`;
        if (drop.glow) {
          ctx.shadowColor = "rgba(120,180,100,0.8)";
          ctx.shadowBlur = 8;
          ctx.fillStyle = `rgba(160,210,140,${drop.opacity * 3})`;
        } else {
          ctx.fillStyle = `rgba(80,110,70,${drop.opacity})`;
        }
        ctx.fillText(drop.char, drop.x, drop.y);
        ctx.restore();
      });
 
      // Occasional quote fragment floating up
      if (frame % 180 === 0 && coins.length > 0) {
        const coin = coins[Math.floor(Math.random() * coins.length)];
        const words = coin.text.split(" ").slice(0, 3).join(" ");
        ctx.save();
        ctx.font = "italic 11px Georgia, serif";
        ctx.fillStyle = "rgba(160,180,140,0.15)";
        ctx.fillText(words + "...", Math.random() * (w - 120) + 20, Math.random() * h);
        ctx.restore();
      }
 
      animRef.current = requestAnimationFrame(draw);
    };
 
    draw();
    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener("resize", resize);
    };
  }, []);
 
  return (
    <canvas ref={canvasRef} style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} />
  );
}
 
// ─── PAGE 1: LANDING ─────────────────────────────────────────────────────────
function LandingPage({ onEnter }) {
  const [vis, setVis] = useState(false);
  useEffect(() => { setTimeout(() => setVis(true), 120); }, []);
 
  return (
    <div style={{ position: "relative", width: "100%", height: "100%", background: "#070908", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
      <CoinRain coins={COINS_DATA} />
      <div style={{
        position: "relative", zIndex: 2, textAlign: "center",
        opacity: vis ? 1 : 0, transform: vis ? "translateY(0)" : "translateY(20px)",
        transition: "all 1.6s ease",
        background: "rgba(7,9,8,0.7)", padding: "48px 40px", backdropFilter: "blur(2px)",
      }}>
        <div style={{ fontFamily: "'Courier New', monospace", letterSpacing: "0.4em", fontSize: 10, color: "#4a6045", marginBottom: 36 }}>
          — &nbsp; S E C R E T &nbsp; L E V E L &nbsp; —
        </div>
        <h1 style={{ fontFamily: "Georgia, serif", fontStyle: "italic", fontWeight: 400, fontSize: "clamp(38px,9vw,66px)", color: "#c8d4b8", margin: "0 0 16px", letterSpacing: "-0.02em", lineHeight: 1.1 }}>
          big stone river
        </h1>
        <div style={{ fontFamily: "'Courier New', monospace", letterSpacing: "0.3em", fontSize: 10, color: "#6a9870", marginBottom: 12 }}>
          T H E &nbsp; R I V E R &nbsp; R E M E M B E R S
        </div>
        <div style={{ fontFamily: "Georgia, serif", fontStyle: "italic", fontSize: 13, color: "#4a5a45", marginBottom: 48 }}>
          throw your coin · make your wish · the river carries it forward
        </div>
        <button onClick={onEnter} style={{
          background: "transparent", border: "1px solid #3a4a38",
          color: "#7a9870", fontFamily: "'Courier New', monospace",
          letterSpacing: "0.3em", fontSize: 10, padding: "16px 44px",
          cursor: "pointer", transition: "all 0.3s",
        }}
          onMouseEnter={e => { e.target.style.borderColor = "#6a9870"; e.target.style.color = "#a8c8a0"; }}
          onMouseLeave={e => { e.target.style.borderColor = "#3a4a38"; e.target.style.color = "#7a9870"; }}
        >
          S T E P &nbsp; I N
        </button>
      </div>
    </div>
  );
}
 
// ─── PAGE 2: RIVER ────────────────────────────────────────────────────────────
function RiverPage({ onArchive, coins, onDropCoin }) {
  const [text, setText] = useState("");
  const [author, setAuthor] = useState("");
  const [dropping, setDropping] = useState(false);
  const [showInput, setShowInput] = useState(false);
  const [wish, setWish] = useState(null);
  const [fadeIn, setFadeIn] = useState(false);
  useEffect(() => { setTimeout(() => setFadeIn(true), 80); }, []);
 
  const handleDrop = async () => {
    if (!text.trim() || dropping) return;
    setDropping(true);
    setShowInput(false);
    setWish("the river is listening...");
    const interp = await interpretCoin(text.trim());
    const coin = { text: text.trim(), author: author.trim() || "anonymous", interpretation: interp, id: Date.now(), timestamp: new Date().toISOString().replace("T", " ").slice(0, 16) + " UTC" };
    onDropCoin(coin);
    setWish(interp);
    setText(""); setAuthor("");
    setTimeout(() => setWish(null), 7000);
    setDropping(false);
  };
 
  return (
    <div style={{ position: "relative", width: "100%", height: "100%", background: "#070908", overflow: "hidden" }}>
      <CoinRain coins={coins} />
 
      {/* Header */}
      <div style={{ position: "absolute", top: 0, left: 0, right: 0, padding: "18px 22px", display: "flex", alignItems: "flex-start", justifyContent: "space-between", zIndex: 10, opacity: fadeIn ? 1 : 0, transition: "opacity 1s ease 0.3s" }}>
        <div style={{ fontFamily: "Georgia, serif", fontStyle: "italic", color: "#c8d4b8", fontSize: 17, lineHeight: 1.25 }}>
          big stone<br />river
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 20 }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ fontFamily: "'Courier New', monospace", fontSize: 8, letterSpacing: "0.25em", color: "#4a5a45" }}>COINS</div>
            <div style={{ fontFamily: "'Courier New', monospace", fontSize: 15, color: "#7a9870", marginTop: 2 }}>{coins.length}</div>
          </div>
          <div style={{ width: 1, height: 26, background: "#252a24" }} />
          <div style={{ textAlign: "center" }}>
            <div style={{ fontFamily: "'Courier New', monospace", fontSize: 8, letterSpacing: "0.25em", color: "#4a5a45" }}>HEARD</div>
            <div style={{ fontFamily: "'Courier New', monospace", fontSize: 15, color: "#7a9870", marginTop: 2 }}>{coins.filter(c => c.interpretation).length}</div>
          </div>
          <div style={{ width: 1, height: 26, background: "#252a24" }} />
          <button onClick={onArchive} style={{ background: "transparent", border: "1px solid #2e3a2c", color: "#7a9870", fontFamily: "'Courier New', monospace", letterSpacing: "0.2em", fontSize: 8, padding: "7px 14px", cursor: "pointer" }}>
            A R C H I V E
          </button>
        </div>
      </div>
 
      {/* Wish echo */}
      {wish && (
        <div style={{
          position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
          zIndex: 15, maxWidth: 340, textAlign: "center",
          background: "rgba(7,9,8,0.88)", border: "1px solid #2a3a28",
          padding: "28px 28px", backdropFilter: "blur(8px)",
        }}>
          <div style={{ fontFamily: "'Courier New', monospace", fontSize: 8, letterSpacing: "0.3em", color: "#4a6a45", marginBottom: 14 }}>
            ∿ &nbsp; T H E &nbsp; R I V E R &nbsp; S P E A K S &nbsp; ∿
          </div>
          <p style={{ fontFamily: "Georgia, serif", fontStyle: "italic", fontSize: 14, color: "#8ab885", lineHeight: 1.7, margin: 0 }}>
            {wish}
          </p>
        </div>
      )}
 
      {/* Bottom */}
      <div style={{ position: "absolute", bottom: 36, left: 0, right: 0, display: "flex", flexDirection: "column", alignItems: "center", zIndex: 10, opacity: fadeIn ? 1 : 0, transition: "opacity 1s ease 0.6s" }}>
        {showInput ? (
          <div style={{ background: "rgba(7,9,8,0.94)", border: "1px solid #2e3a2c", padding: "20px 22px", width: "min(360px, 90vw)", backdropFilter: "blur(10px)" }}>
            <div style={{ fontFamily: "'Courier New', monospace", fontSize: 8, letterSpacing: "0.3em", color: "#4a6a45", marginBottom: 12 }}>⊙ &nbsp; T H R O W &nbsp; Y O U R &nbsp; C O I N</div>
            <textarea autoFocus value={text} onChange={e => setText(e.target.value)}
              placeholder="your quote, word or wish..."
              style={{ width: "100%", background: "transparent", border: "none", outline: "none", fontFamily: "Georgia, serif", fontStyle: "italic", fontSize: 15, color: "#c8d4b8", resize: "none", minHeight: 56, caretColor: "#6a9870" }}
              onKeyDown={e => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleDrop(); } }}
            />
            <input value={author} onChange={e => setAuthor(e.target.value)}
              placeholder="who said it? (leave blank = anonymous)"
              style={{ width: "100%", background: "transparent", border: "none", borderTop: "1px solid #1e2820", outline: "none", fontFamily: "'Courier New', monospace", fontSize: 9, color: "#5a6a55", padding: "8px 0 0", marginTop: 8, letterSpacing: "0.05em" }}
            />
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 14 }}>
              <button onClick={() => setShowInput(false)} style={{ background: "none", border: "none", color: "#4a5a45", fontFamily: "'Courier New', monospace", fontSize: 8, letterSpacing: "0.2em", cursor: "pointer" }}>CANCEL</button>
              <button onClick={handleDrop} disabled={dropping} style={{ background: "none", border: "1px solid #4a6a45", color: "#7a9870", fontFamily: "'Courier New', monospace", fontSize: 8, letterSpacing: "0.2em", padding: "6px 16px", cursor: "pointer" }}>
                {dropping ? "..." : "⊙ &nbsp;THROW"}
              </button>
            </div>
          </div>
        ) : (
          <button onClick={() => setShowInput(true)} style={{ background: "transparent", border: "none", fontFamily: "Georgia, serif", fontStyle: "italic", fontSize: 14, color: "#4a5a45", cursor: "pointer", transition: "color 0.3s" }}
            onMouseEnter={e => e.target.style.color = "#8ab885"}
            onMouseLeave={e => e.target.style.color = "#4a5a45"}
          >
            ⊙ &nbsp; throw your coin · make a wish
          </button>
        )}
      </div>
    </div>
  );
}
 
// ─── PAGE 3: ARCHIVE ─────────────────────────────────────────────────────────
function ArchivePage({ onClose, coins }) {
  const [selected, setSelected] = useState(null);
  const [vis, setVis] = useState(false);
  useEffect(() => { setTimeout(() => setVis(true), 80); }, []);
 
  const coin = selected !== null ? coins[selected] : null;
 
  return (
    <div style={{ width: "100%", height: "100%", background: "#070908", color: "#c8d4b8", overflowY: "auto", opacity: vis ? 1 : 0, transition: "opacity 0.5s ease" }}>
      <div style={{ maxWidth: 500, margin: "0 auto", padding: "24px 22px 60px" }}>
 
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 36 }}>
          <div>
            <div style={{ fontFamily: "'Courier New', monospace", letterSpacing: "0.3em", fontSize: 8, color: "#4a5a45", marginBottom: 6 }}>ALL THERE IS</div>
            <h2 style={{ fontFamily: "Georgia, serif", fontStyle: "italic", fontWeight: 400, fontSize: "clamp(20px,5vw,28px)", color: "#c8d4b8", margin: 0, lineHeight: 1.2 }}>
              {coin ? coin.text : "every coin ever thrown"}
            </h2>
          </div>
          <button onClick={coin ? () => setSelected(null) : onClose} style={{ background: "transparent", border: "1px solid #2e3a2c", color: "#7a9870", fontFamily: "'Courier New', monospace", fontSize: 8, letterSpacing: "0.2em", padding: "8px 14px", cursor: "pointer", whiteSpace: "nowrap" }}>
            {coin ? "← BACK" : "C L O S E"}
          </button>
        </div>
 
        {coin ? (
          /* Detail view */
          <div>
            <div style={{ fontFamily: "'Courier New', monospace", fontSize: 8, letterSpacing: "0.2em", color: "#4a5a45", marginBottom: 6 }}>{coin.timestamp}</div>
            <div style={{ fontFamily: "Georgia, serif", fontSize: 11, color: "#6a9870", marginBottom: 20, letterSpacing: "0.15em" }}>— {coin.author} —</div>
            <blockquote style={{ borderLeft: "2px solid #2a3828", margin: "0 0 28px", paddingLeft: 18, fontFamily: "Georgia, serif", fontStyle: "italic", fontSize: 15, color: "#7ab87a", lineHeight: 1.75 }}>
              {coin.interpretation}
            </blockquote>
            <hr style={{ border: "none", borderTop: "1px solid #181c18", margin: "24px 0" }} />
            <div style={{ fontFamily: "'Courier New', monospace", fontSize: 8, letterSpacing: "0.3em", color: "#3a4a38" }}>∿ &nbsp; THE RIVER CARRIES THIS FORWARD &nbsp; ∿</div>
          </div>
        ) : (
          /* List view */
          coins.map((c, i) => (
            <div key={c.id || i}>
              <div onClick={() => setSelected(i)} style={{ cursor: "pointer", paddingBottom: 24 }}>
                <div style={{ fontFamily: "'Courier New', monospace", fontSize: 8, letterSpacing: "0.2em", color: "#4a5a45", marginBottom: 6 }}>{c.timestamp}</div>
                <h3 style={{ fontFamily: "Georgia, serif", fontWeight: 400, fontSize: 18, color: "#c8d4b8", margin: "0 0 4px", transition: "color 0.2s" }}
                  onMouseEnter={e => e.currentTarget.style.color = "#e8f0d8"}
                  onMouseLeave={e => e.currentTarget.style.color = "#c8d4b8"}
                >{c.text}</h3>
                <div style={{ fontFamily: "'Courier New', monospace", fontSize: 8, color: "#4a6a45", marginBottom: 10 }}>— {c.author}</div>
                <blockquote style={{ borderLeft: "2px solid #1e2a1c", margin: 0, paddingLeft: 14, fontFamily: "Georgia, serif", fontStyle: "italic", fontSize: 13, color: "#5a8060", lineHeight: 1.65 }}>
                  {c.interpretation}
                </blockquote>
              </div>
              <hr style={{ border: "none", borderTop: "1px solid #141814", margin: "0 0 24px" }} />
            </div>
          ))
        )}
 
        {!coin && coins.length === 0 && (
          <div style={{ textAlign: "center", padding: "80px 0", fontFamily: "Georgia, serif", fontStyle: "italic", color: "#2a3828", fontSize: 15 }}>
            no coins yet. step into the river.
          </div>
        )}
      </div>
    </div>
  );
}
 
// ─── ROOT ─────────────────────────────────────────────────────────────────────
export default function App() {
  const [page, setPage] = useState("landing");
  const [coins, setCoins] = useState(COINS_DATA);
 
  const handleDropCoin = useCallback((coin) => {
    setCoins(prev => [coin, ...prev]);
  }, []);
 
  return (
    <div style={{ width: "100vw", height: "100dvh", overflow: "hidden", position: "relative", background: "#070908" }}>
      {page === "landing" && <LandingPage onEnter={() => setPage("river")} />}
      {page === "river" && <RiverPage onArchive={() => setPage("archive")} coins={coins} onDropCoin={handleDropCoin} />}
      {page === "archive" && <ArchivePage onClose={() => setPage("river")} coins={coins} />}
    </div>
  );
}
 